import { useAuth } from '@/contexts/AuthContext';
import { getRoleHomeRoute } from '@/lib/role-redirect';
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { UserRole } from '@/types';

interface Props {
  children: React.ReactNode;
  /** At least one of these roles must be present in the user's propertyRoles. */
  roles: UserRole[];
  redirectTo?: string;
}

/**
 * Extends authentication check with a role requirement.
 * Redirects unauthenticated users to /login.
 * Redirects authenticated users who lack the required role to their own home
 * dashboard (avoids the two-hop /login → home redirect for super_admin and
 * cross-role navigation attempts).
 */
export function RoleProtectedRoute({ children, roles, redirectTo = '/login' }: Props) {
  const { isLoading, isAuthenticated, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="size-8 animate-spin rounded-full border-2 border-border border-t-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  const hasRole = user?.propertyRoles.some((r) => roles.includes(r.role));
  if (!hasRole) {
    // Send the user to their own dashboard instead of /login to avoid an
    // unnecessary round-trip through the login page.
    const home = user ? getRoleHomeRoute(user) : redirectTo;
    // Safety guard: never redirect to the current path. If getRoleHomeRoute
    // returns the same path we're already protecting (data inconsistency),
    // fall back to /login to break the loop instead of looping infinitely.
    if (home === location.pathname) {
      return <Navigate to="/login" replace />;
    }
    return <Navigate to={home} replace />;
  }

  return <>{children}</>;
}
